import { useEffect, useMemo, useRef, useState } from "react";
import styled from "styled-components";
import { Spin, Typography } from "antd";
import { useTranslate } from "@refinedev/core";
import type { SectionWithSlides } from "hooks/useStoryboardWithSlides/";
import { StreamingSlidePreview } from "pages/media/projects/components/slides/";
import type {
  Templates,
  TemplateState,
} from "pages/media/components/storyboardEditor/types/";
import type { useBrandKit } from "hooks/useBrandKit/";
import type { Slide } from "pages/media/types/";
import { radius } from "@libertify/ui";

const BASE_RENDER_WIDTH = 480;

type SlideCarouselPreviewProps = {
  sections: SectionWithSlides[];
  templates: Templates;
  sectionTemplateIds?: string[];
  brandKitData: ReturnType<typeof useBrandKit>;
  mediaAspectRatio: number;
};

export const SlideCarouselPreview: React.FC<SlideCarouselPreviewProps> = ({
  sections,
  templates,
  sectionTemplateIds,
  brandKitData,
  mediaAspectRatio,
}) => {
  const t = useTranslate();
  const [activeScene, setActiveScene] = useState(0);

  type CarouselItem = {
    slide: Slide;
    template: TemplateState;
    chapterType?: string;
    previewText?: string;
  };

  const storyboardSlides = useMemo(
    () => sections.flatMap((s) => s.slides),
    [sections]
  );

  const dummySlide = (key: string, topic?: string): Slide =>
    ({
      uuid: key,
      id: key,
      slide_type: "bullet_point",
      section_id: null,
      placeholders: topic ? { title: topic, subtitle: topic } : {},
    }) as unknown as Slide;

  // Custom projects: map each section to its template by ID (same as the
  // storyboard editor). This path is used when sectionTemplateIds is
  // available and produces matches in the templates Map.
  const sectionEntries = useMemo((): CarouselItem[] => {
    if (
      !sectionTemplateIds ||
      sectionTemplateIds.length === 0 ||
      templates.size === 0
    )
      return [];
    return sectionTemplateIds.reduce<CarouselItem[]>((acc, templateId, i) => {
      const template = templates.get(templateId);
      if (template) acc.push({ slide: dummySlide(`section_${i}`), template });
      return acc;
    }, []);
  }, [sectionTemplateIds, templates]);

  // Flash projects: map each real slide to its template by slide name/UUID.
  const slideEntries = useMemo((): CarouselItem[] => {
    if (storyboardSlides.length === 0) return [];
    return storyboardSlides.reduce<CarouselItem[]>((acc, slide) => {
      const template = templates.get(slide.uuid);
      if (template) acc.push({ slide, template });
      return acc;
    }, []);
  }, [storyboardSlides, templates]);

  // Skip mainImage (needs real slide asset_id) but allow parametered texts
  // (we populate title/subtitle from section topics in dummy slides).
  const hasNoRealSlides =
    sectionEntries.length > 0 || storyboardSlides.length === 0;

  // Build carousel entries from sections using brand kit type templates
  // to mirror the editor's intro → transition → chapter → ... → closing order.
  const sectionFallbackEntries = useMemo((): CarouselItem[] => {
    if (templates.size === 0 || sections.length === 0) return [];

    const intro = templates.get("default_Introduction");
    const chapter = templates.get("default_Chapter");
    const transition = templates.get("default_Transition");
    const closing = templates.get("default_Closing");

    const items: CarouselItem[] = [];
    if (intro)
      items.push({
        slide: dummySlide("intro"),
        template: intro,
        chapterType: "Introduction",
      });
    sections.forEach((section, i) => {
      if (transition)
        items.push({
          slide: dummySlide(`transition_${i}`),
          template: transition,
          chapterType: "Transition",
          previewText: section.topic,
        });
      if (chapter)
        items.push({
          slide: dummySlide(`chapter_${i}`),
          template: chapter,
          chapterType: "Chapter",
          previewText: section.topic,
        });
    });
    if (closing)
      items.push({
        slide: dummySlide("closing"),
        template: closing,
        chapterType: "Closing",
      });
    return items;
  }, [templates, sections]);

  // Pick the best available entries:
  // 1. Section→template mapping (custom projects with media settings in cache)
  // 2. Slide→template mapping (flash projects)
  // 3. Section-based brand kit fallback (custom projects, mirrors editor order)
  const carouselItems = useMemo((): CarouselItem[] => {
    if (sectionEntries.length > 0) return sectionEntries;
    if (slideEntries.length > 0) return slideEntries;
    return sectionFallbackEntries;
  }, [sectionEntries, slideEntries, sectionFallbackEntries]);

  const containerRef = useRef<HTMLDivElement>(null);
  const [containerWidth, setContainerWidth] = useState(BASE_RENDER_WIDTH);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = () => setContainerWidth(el.clientWidth);
    update();
    const observer = new ResizeObserver(update);
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  const templateSize = useMemo(
    () => ({
      width: containerWidth,
      height: Math.round(containerWidth / mediaAspectRatio),
    }),
    [containerWidth, mediaAspectRatio]
  );

  useEffect(() => {
    if (carouselItems.length === 0) return;
    const iv = setInterval(
      () => setActiveScene((p) => (p + 1) % carouselItems.length),
      3000
    );
    return () => clearInterval(iv);
  }, [carouselItems.length]);

  if (carouselItems.length === 0) return null;

  return (
    <StyledContainer ref={containerRef}>
      <StyledSlideLoopWrap style={templateSize}>
        {carouselItems.map((item, i) => (
          <StyledSlideOverlay key={item.slide.uuid} $active={i === activeScene}>
            <StreamingSlidePreview
              templateSize={templateSize}
              slide={item.slide}
              template={item.template}
              brandKitData={brandKitData}
              skipSlideContent={hasNoRealSlides}
              chapterType={item.chapterType}
              previewText={item.previewText}
              onCardClick={() => undefined}
              border={{ borderWidth: 0, borderRadius: 0 }}
            />
          </StyledSlideOverlay>
        ))}
        <StyledBlurOverlay>
          <Spin size="large" />
          <Typography.Text
            style={{
              marginTop: 12,
              fontSize: 13,
              textAlign: "center",
              color: "rgba(255,255,255,0.6)",
              maxWidth: 300,
            }}
          >
            {t("media.projects.detail.videoGeneratingHint")}
          </Typography.Text>
        </StyledBlurOverlay>
      </StyledSlideLoopWrap>
    </StyledContainer>
  );
};

const StyledContainer = styled.div`
  display: flex;
  justify-content: center;
  align-items: center;
  height: 100%;
  width: 100%;
  overflow: hidden;
`;

const StyledSlideLoopWrap = styled.div`
  position: relative;
  border-radius: ${radius.box}px;
  overflow: hidden;
  background: #111827;
`;

const StyledBlurOverlay = styled.div`
  position: absolute;
  inset: 0;
  z-index: 10;
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
  backdrop-filter: blur(6px);
  background: rgba(0, 0, 0, 0.5);
  cursor: default;
`;

const StyledSlideOverlay = styled.div<{ $active: boolean }>`
  position: absolute;
  inset: 0;
  opacity: ${(p) => (p.$active ? 1 : 0)};
  transition: opacity 0.8s ease;
`;
